import type {
  CreatorLanguage,
  OutreachAngle,
  OutreachFilter,
  OutreachGroupInput,
  OutreachPreviewGroup,
  OutreachSplit,
  OutreachTone,
  PoolSource,
} from '@/api/client'
import { STARTER, checkTemplate } from '@/lib/creators'

export interface GroupDraft {
  offer: string
  angle: OutreachAngle
  tone: OutreachTone
  /** null = untouched: the starter text for the language and product shows. */
  template: string | null
  personalize: boolean
}

export type SplitMethod = 'one' | 'followers' | 'random'

/** Everything the four steps edit. It lives in the page, so going back and
 *  forth keeps every field. */
export interface WizardState {
  poolId: number
  fMin: string
  fMax: string
  score: string
  /** Percent, as typed; the API takes a fraction. */
  engagement: string
  sources: PoolSource[]
  picked: number[]
  method: SplitMethod
  boundaries: string
  randomGroups: number
  productName: string
  productUrl: string
  productBrief: string
  language: CreatorLanguage
  signoff: string
  groups: Record<string, GroupDraft>
  name: string
  daily: string
  startHour: string
  endHour: string
  timezone: string
  sender: string
}

export const newState = (poolId = 0): WizardState => ({
  poolId,
  fMin: '',
  fMax: '',
  score: '',
  engagement: '',
  sources: [],
  picked: [],
  method: 'one',
  boundaries: '5000, 50000',
  randomGroups: 2,
  productName: '',
  productUrl: '',
  productBrief: '',
  language: 'ko',
  signoff: '',
  groups: {},
  name: '',
  daily: '10',
  startHour: '10',
  endHour: '20',
  timezone: 'Asia/Seoul',
  sender: '',
})

export const defaultGroup = (): GroupDraft => ({ offer: '', angle: 'review', tone: 'polite', template: null, personalize: true })

export const templateOf = (s: WizardState, g: GroupDraft | undefined) =>
  g?.template ?? (STARTER[s.language] ?? STARTER.en)(s.productName.trim())

const num = (v: string): number | undefined | null => {
  const t = v.trim()
  if (t === '') return undefined
  const n = Number(t)
  return Number.isFinite(n) && n >= 0 ? n : null
}

/** The filter the preview and the create call send, or the reason it cannot be built. */
export function buildFilter(s: WizardState): { value: OutreachFilter; error: string | null } {
  const f: OutreachFilter = {}
  let error: string | null = null
  const lo = num(s.fMin)
  const hi = num(s.fMax)
  const sc = num(s.score)
  const en = num(s.engagement)
  if (lo === null) error = '팔로워 최소는 0 이상의 숫자다.'
  else if (hi === null) error = '팔로워 최대는 0 이상의 숫자다.'
  else if (lo !== undefined && hi !== undefined && hi < lo) error = '팔로워 최대는 최소보다 작을 수 없다.'
  else if (sc === null || (sc !== undefined && sc > 100)) error = '최소 점수는 0~100이다.'
  else if (en === null || (en !== undefined && en > 100)) error = '최소 참여율은 0~100%다.'
  if (lo !== undefined && lo !== null) f.min_followers = lo
  if (hi !== undefined && hi !== null) f.max_followers = hi
  if (sc !== undefined && sc !== null) f.min_score = sc
  if (en !== undefined && en !== null) f.min_engagement = en / 100
  if (s.sources.length > 0) f.sources = s.sources
  if (s.picked.length > 0) f.creator_ids = s.picked
  return { value: f, error }
}

/** 1–3 ascending whole numbers, or why not. */
export function parseBoundaries(text: string): { value: number[]; error: string | null } {
  const parts = text
    .split(/[,\s]+/)
    .map((p) => p.trim())
    .filter(Boolean)
  const value = parts.map(Number)
  if (parts.length < 1 || parts.length > 3) return { value, error: '경계는 1~3개를 쉼표로 나눠 적는다.' }
  if (value.some((n) => !Number.isInteger(n) || n <= 0)) return { value, error: '경계는 1 이상의 정수다.' }
  for (let i = 1; i < value.length; i++) if (value[i] <= value[i - 1]) return { value, error: '경계는 작은 수부터 커지는 순서로 적는다.' }
  return { value, error: null }
}

export function buildSplit(s: WizardState): { value: OutreachSplit; error: string | null } {
  if (s.method === 'one') return { value: { method: 'one' }, error: null }
  if (s.method === 'random') return { value: { method: 'random', groups: s.randomGroups }, error: null }
  const b = parseBoundaries(s.boundaries)
  return { value: { method: 'followers', boundaries: b.value }, error: b.error }
}

export function isUrl(v: string): boolean {
  try {
    const u = new URL(v.trim())
    return u.protocol === 'http:' || u.protocol === 'https:'
  } catch {
    return false
  }
}

export function messageProblem(s: WizardState, groups: OutreachPreviewGroup[], platform?: 'instagram' | 'bluesky'): string | null {
  if (!s.productName.trim()) return '상품 이름을 적는다.'
  if (!isUrl(s.productUrl)) return '상품 주소는 http:// 또는 https://로 시작하는 주소다.'
  for (const g of groups) {
    const d = s.groups[g.key] ?? defaultGroup()
    if (!d.offer.trim()) return `${g.label} 그룹의 제안을 적는다.`
    const c = checkTemplate(templateOf(s, d), s.language, platform)
    if (!c.ok) return `${g.label} 그룹: ${c.problems[0]}`
  }
  return null
}

const wholeIn = (v: string, lo: number, hi: number) => /^\d+$/.test(v.trim()) && Number(v) >= lo && Number(v) <= hi

export function sendProblem(s: WizardState, bluesky: boolean): string | null {
  if (!s.name.trim()) return '발송 이름을 적는다.'
  if (!wholeIn(s.daily, 1, 30)) return '하루 한도는 1~30이다.'
  if (!wholeIn(s.startHour, 0, 23) || !wholeIn(s.endHour, 1, 24)) return '보내는 시간은 0~24시 사이다.'
  if (Number(s.endHour) <= Number(s.startHour)) return '끝나는 시각은 시작보다 늦어야 한다.'
  if (!s.timezone.trim()) return '시간대를 고른다.'
  if (bluesky && !s.sender) return 'Bluesky 보내는 계정을 고른다.'
  return null
}

export function groupInputs(s: WizardState, groups: OutreachPreviewGroup[]): OutreachGroupInput[] {
  return groups.map((g) => {
    const d = s.groups[g.key] ?? defaultGroup()
    return {
      key: g.key,
      label: g.label,
      offer: d.offer.trim(),
      angle: d.angle,
      tone: d.tone,
      template: templateOf(s, d).trim(),
      personalize: d.personalize,
    }
  })
}
